import type { Calendar, InstagramStatus, PostList, PublicWall, Wall } from "@brandwall/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { useMe } from "../App";
import { api, post } from "../api";
import { ageLabel } from "../freshness";

export function Home() {
  const { ws } = useParams();
  const me = useMe();
  const first = (me.data?.user.name ?? "").split(" ")[0];
  const inbox = useQuery({
    queryKey: ["counts", ws, "posts"],
    queryFn: () => api<PostList>(`/v1/w/${ws}/posts?tab=to_review&limit=1`),
  });
  const ig = useQuery({
    queryKey: ["instagram", ws],
    queryFn: () => api<InstagramStatus>(`/v1/w/${ws}/instagram`),
  });
  const walls = useQuery({
    queryKey: ["walls", ws],
    queryFn: () => api<{ walls: Wall[] }>(`/v1/w/${ws}/walls`),
  });
  const main =
    walls.data?.walls.find((w) => w.status === "live" && w.kind === "normal") ??
    walls.data?.walls.find((w) => w.kind === "normal");
  const preview = useQuery({
    queryKey: ["wall-preview", ws, main?.id],
    queryFn: () => api<PublicWall>(`/v1/w/${ws}/walls/${main!.id}/preview`),
    enabled: Boolean(main),
  });
  const calendar = useQuery({
    queryKey: ["schedules", ws],
    queryFn: () => api<Calendar>(`/v1/w/${ws}/schedules`),
  });
  const nextSeason = calendar.data?.schedules.find(
    (s) => s.state === "live" || s.state === "upcoming" || s.state === "waiting",
  );
  const toReview = inbox.data?.counts.to_review ?? 0;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const age = main?.freshness.newestAgeDays ?? null;
  const score = main?.freshness.score ?? 0;

  const reconnect = async () => {
    const { url } = await post<{ url: string }>(`/v1/w/${ws}/instagram/connect`);
    window.location.href = url;
  };

  return (
    <div className="stack" style={{ gap: 20 }}>
      <div>
        <p className="mono small muted">
          {new Date()
            .toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })
            .toUpperCase()}
        </p>
        <h1 style={{ fontSize: 28 }}>
          {greeting}
          {first ? `, ${first}` : ""}
        </h1>
      </div>

      {ig.data && !ig.data.connected && (
        <div className="card row wrap">
          <div className="spacer">
            <h2>Connect Instagram</h2>
            <p className="muted">
              Connect your brand's Instagram account to collect the posts people share about you.
            </p>
          </div>
          <Link className="btn" to={`/w/${ws}/connect`}>
            Connect Instagram
          </Link>
        </div>
      )}
      {ig.data?.status === "broken" && (
        <div className="card row wrap" style={{ borderColor: "var(--berry)" }} role="alert">
          <div className="spacer">
            <h2>Instagram stopped sending us new posts</h2>
            <p className="muted">
              Your walls still show the posts they have. Reconnect to get new posts again.
            </p>
          </div>
          <button type="button" className="btn berry" onClick={reconnect}>
            Reconnect Instagram
          </button>
        </div>
      )}

      <div className="grid2">
        <div className="card stack">
          <span className="mono small muted">TO REVIEW</span>
          <div className="stat">
            {toReview}{" "}
            <span className="small muted" style={{ fontWeight: 600 }}>
              new posts
            </span>
          </div>
          <Link className="btn" to={`/w/${ws}/inbox`}>
            {toReview ? `Review ${toReview} new post${toReview === 1 ? "" : "s"}` : "Open inbox"}
          </Link>
        </div>
        <div className="card stack">
          <span className="mono small muted">{main ? main.name.toUpperCase() : "YOUR WALL"}</span>
          {main ? (
            <>
              <div className="row">
                <span className={`pill ${main.status === "live" ? "ok" : ""}`}>
                  {main.status === "live" ? (
                    <>
                      <span className="dot" /> Live
                    </>
                  ) : (
                    "Draft"
                  )}
                </span>
                <span className="small muted">
                  {age === null
                    ? "No posts yet"
                    : `Newest post is ${age === 0 ? "from today" : `${age} day${age === 1 ? "" : "s"} old`}`}
                </span>
              </div>
              <div className="row">
                <span className="stat">{score}</span>
                <span className="muted">/ 100 freshness</span>
              </div>
              {main.freshness.averageAgeDays !== null && (
                <p className="small muted">
                  Posts on this wall are {main.freshness.averageAgeDays} days old on average
                </p>
              )}
              {main.freshness.tip && (
                <p className="small" style={{ color: "var(--warn)" }}>
                  {main.freshness.tip}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="muted">
                A wall shows your best posts on your website. You don't have one yet.
              </p>
              <Link className="btn" to={`/w/${ws}/walls`}>
                Make your first wall
              </Link>
            </>
          )}
        </div>
      </div>

      <div className="card row wrap">
        <div className="spacer">
          <span className="mono small muted">NEXT SEASONAL WALL</span>
          {nextSeason ? (
            <p style={{ marginTop: 4 }}>
              <b>{nextSeason.wallName}</b>{" "}
              {nextSeason.state === "live"
                ? `is live on ${nextSeason.page} until ${new Date(`${nextSeason.endsOn}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
                : `goes live on ${nextSeason.page} on ${new Date(`${nextSeason.startsOn}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}`}
              {nextSeason.state === "waiting" ? ", once you approve it" : ""}.
            </p>
          ) : (
            <p className="muted" style={{ marginTop: 4 }}>
              Nothing planned. Get a wall ready ahead of Halloween, Black Friday or Christmas.
            </p>
          )}
        </div>
        <Link className="btn secondary" to={`/w/${ws}/seasons`}>
          {nextSeason ? "See the calendar" : "Plan a season"}
        </Link>
      </div>

      {preview.data && preview.data.posts.length > 0 && (
        <div className="card stack">
          <div className="row">
            <h2 className="spacer">Your {main?.name} right now</h2>
            <span className="small muted">{preview.data.posts.length} posts live</span>
          </div>
          <div className="thumbs">
            {preview.data.posts.slice(0, 6).map((p) => (
              <div className="t" key={p.id}>
                <img src={p.src} alt={p.alt} loading="lazy" />
                <span>{ageLabel(p.postedAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
